import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import {
  Content,
  Header,
  Table,
  TableColumn,
  Progress,
  CreateButton,
  Link,
} from '@backstage/core-components';
import { 
  Box, 
  Typography, 
  makeStyles,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Sync';
import MoreIcon from '@material-ui/icons/MoreHoriz';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { BackstageTheme } from '@backstage/theme';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { tablePaginationLocalization } from '../../i18n/pagination';

interface Notification {
  description: string;
  startdatetime: string;
  notificationtype?: string;
  isscheduled?: boolean;
  status?: boolean;
}

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

const useStyles = makeStyles(
  theme => ({
    root: {
      "& $actions": {
        paddingRight: theme.spacing(1),
      },
      "& table $actions": {
        paddingRight: theme.spacing(0),
      },
    },
    actions: {
      display: "flex",
      justifyContent: "right"
    },
    noDataMessage: {
      padding: theme.spacing(2),
      display: "flex",
      justifyContent: "center",
    },
    buttonContainer: { 
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      textAlign: 'left',
      alignItems: 'center',
      marginBottom: '16px',
      flexDirection: 'row',
      justifyContent: 'flex-end'
    }
  }),
  { name: 'NotificationsList' },
);

const NotificationsList = () => {
  const { t, formatUtcDateTime } = useServiceRegistryTranslation();
  const classes = useStyles();
  const theme = useTheme<BackstageTheme>();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const identityApi = useApi(identityApiRef);
  const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const tableRef = useRef<any>();

  // Initialize with default sort configuration
  const [orderBy, setOrderBy] = useState<string>('startdatetime');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');

  // Fetch user role on component mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: profile.email }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setUserRole(userRoleResponse);
      } catch (error) {
        // console.error('Error fetching user role:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [identityApi, baseUrl]);

  // Handle data fetching with server-side pagination, searching, and sorting
  const fetchData = useCallback(async (query: any) => {
    if (!userRole) return { data: [], page: 0, totalCount: 0 };

    try {
      setLoading(true);
      const userType = userRole.roleName === 'Admin' ? 'admin' : 'subscriber';
      const { token } = await identityApi.getCredentials();
      
      // FIXED: Better handling of sort parameters
     
      const sortField = query.orderBy?.field || orderBy;
      const sortDirection = query.orderDirection || orderDirection;
      
      // FIXED: Prevent unnecessary state updates that trigger re-renders
      // Only update state if values actually changed
      if (sortField !== orderBy || sortDirection !== orderDirection) {
        setOrderBy(sortField);
        setOrderDirection(sortDirection);
      }
      
      // Improved field mapping
      const getActualSortField = (field: string | null) => {
        if (!field) return 'startdatetime';
        
        const fieldMap: Record<string, string> = {
          'notificationtype': 'notificationtype',
          'description': 'description',
          'startdatetime': 'startdatetime',
          'isscheduled': 'isscheduled',
          'status': 'status'
        };
        
        const mappedField = fieldMap[field];
        console.log(`Mapping sort field: ${field} -> ${mappedField || 'startdatetime'}`);
        return mappedField || 'startdatetime';
      };
      
      const backendSortField = getActualSortField(sortField);
      const searchParam = query.search ? `&searchTerm=${encodeURIComponent(query.search)}` : '';
      
      const url = `${baseUrl}/api/notifications/view?pageNumber=${query.page + 1}&pageSize=${query.pageSize}${searchParam}&sortFieldName=${backendSortField}&sortDirection=${sortDirection}`;
      
      console.log('Fetching notifications with URL:', url);
      console.log('Sort parameters:', { backendSortField, sortDirection, originalField: sortField });
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const { data: notificationsData, pagination } = data;

      return {
        data: notificationsData,
        page: query.page,
        totalCount: pagination.totalCount,
      };
    } catch (error) {
      // console.error('Error fetching notifications:', error);
      return { data: [], page: 0, totalCount: 0 };
    } finally {
      setLoading(false);
    }
  }, [userRole, baseUrl, identityApi, orderBy, orderDirection]);

  // FIXED: Simplified sort handler to prevent double requests
  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    console.log(`Handling sort: field=${field}, direction=${direction}`);
    
    // Update state immediately to prevent conflicts
    setOrderBy(field);
    setOrderDirection(direction);
    
    // The table will automatically call fetchData with the new sort parameters
  }, []);

  // Define columns based on user role
  const columns: TableColumn<Notification>[] =
    userRole?.roleName === 'Admin'
      ? [
          {
            title: t('notifications.type'),
            field: 'notificationtype',
            render: (row: Partial<Notification>) => (
              <Typography variant="body2">{row.notificationtype || t('notifications.notAvailable')}</Typography>
            ),
            sorting: true,
            // FIXED: Removed defaultSort to prevent conflicts
          },
          {
            title: t('notifications.messages'),
            field: 'description',
            render: (row: Partial<Notification>) => (
              <Typography variant="body2">{row.description}</Typography>
            ),
            cellStyle: {
              fontWeight: theme.typography.fontWeightMedium,
            },
            sorting: true,
          },
          {
            title: t('notifications.scheduled'),
            field: 'isscheduled',
            render: (row: Partial<Notification>) => (
              <Typography variant="body2">{row.isscheduled ? t('notifications.yes') : t('notifications.no')}</Typography>
            ),
            sorting: true,
          },
          {
            title: t('notifications.scheduledTime'),
            field: 'startdatetime',
            render: (row: Partial<Notification>) => {
              try {
                if (!row.startdatetime) {
                  return <Typography variant="body2">{t('notifications.noDate')}</Typography>;
                }
                return (
                  <Typography variant="body2">
                    {formatUtcDateTime(row.startdatetime as string)}
                  </Typography>
                );
              } catch (e) {
                return <Typography variant="body2">{t('notifications.invalidDate')}</Typography>;
              }
            },
            sorting: true,
            defaultSort: 'desc',
          },
        ]
      : [
        {
            title: t('notifications.type'),
            field: 'notificationtype',
            render: (row: Partial<Notification>) => (
              <Typography variant="body2">{row.notificationtype || t('notifications.notAvailable')}</Typography>
            ),
            sorting: true,
          },
          {
            title: t('notifications.messages'),
            field: 'description',
            render: (row: Partial<Notification>) => (
              <Typography variant="body2">{row.description}</Typography>
            ),
            cellStyle: {
              fontWeight: theme.typography.fontWeightMedium,
            },
            sorting: true,
          },
          {
            title: t('notifications.dateTime'),
            field: 'startdatetime',
            render: (row: Partial<Notification>) => {
              try {
                if (!row.startdatetime) {
                  return <Typography variant="body2">{t('notifications.noDate')}</Typography>;
                }
                return (
                  <Typography variant="body2">
                    {formatUtcDateTime(row.startdatetime as string)}
                  </Typography>
                );
              } catch (e) {
                return <Typography variant="body2">{t('notifications.invalidDate')}</Typography>;
              }
            },
            sorting: true,
            defaultSort: 'desc',
          },
        ];

  if (loading && !userRole) {
    return <Progress />;
  }

  return (
    <>
      <Header
        title={t('notifications.title')}
        style={{ margin: 0 }}
      />
      <Content>
        <div className={classes.root}>
          {userRole?.roleName === 'Admin' && (
            <Box mr={2} className={classes.buttonContainer}>
              <CreateButton
                title={t('notifications.add')}
                to={'/notifications/add'}
              />
            </Box>
          )}
          
          <div className={classes.root}>
            <Table<Notification>
              options={{
                paging: true,
                pageSize: 20,
                pageSizeOptions: [10, 20, 50, 100],
                emptyRowsWhenPaging: false,
                search: true,
                searchDebounceDelay: 500,
                filtering: false,
                padding: 'dense',
                loadingType: 'linear',
                showEmptyDataSourceMessage: !loading,
                paginationType: "normal",
                sorting: true,
                thirdSortClick: false,
                actionsColumnIndex: -1,
                headerStyle: {
                  fontWeight: theme.typography.fontWeightBold,
                  fontSize: '0.875rem',
                },
                rowStyle: {
                  fontSize: '0.875rem',
                },
                // FIXED: Only set initial sorting, don't override user sorting
                // initialSorting: [{ field: 'startdatetime', order: 'desc' }]
              }}
              data={fetchData}
              columns={columns}
              tableRef={tableRef}
              onOrderChange={handleSort}
              icons={{
                ...Table.icons,
                Search: forwardRef((iconProps, ref) => (
                  <SearchIcon {...iconProps} ref={ref} />
                )),
                Refresh: forwardRef((iconProps, ref) => (
                  <RefreshIcon {...iconProps} ref={ref} />
                )),
              }}
              actions={[
                {
                  icon: () => <RefreshIcon />,
                  tooltip: t('notifications.refresh'),
                  isFreeAction: true,
                  onClick: () => {
                    tableRef.current.onQueryChange();
                  },
                },
              ]}
              localization={{
                pagination: tablePaginationLocalization(t),
                toolbar: { searchPlaceholder: t('notifications.search'), searchTooltip: '' },
                header: { actions: '' },
                body: {
                  emptyDataSourceMessage: (
                    <Typography color="textSecondary" className={classes.noDataMessage}>
                      {t('notifications.empty')}
                    </Typography>
                  )
                },
              }}
            />
          </div>
        </div>
      </Content>
    </>
  );
};

export default NotificationsList;
